import React from 'react'
import { Col, Row } from 'antd'
import styled from '@emotion/styled'
import type { Application } from 'renter/interfaces/api/application'

import ApplicantInfo from 'shared/components/CreditAndBackgroundCheck/components/ApplicantInfo'
import { EvictionsType } from 'shared/components/CreditAndBackgroundCheck/interfaces'
import Evictions from 'shared/components/CreditAndBackgroundCheck/components/Evictions'
import { isEvictionReportPending } from 'shared/components/CreditAndBackgroundCheck/utils/reportStatusHelpers'
import { formatDate } from 'shared/utils/ui'
import { printDollarsFromCents } from 'shared/utils/dollar-print'
import ResidentialHistory from 'shared/components/Applicant/ResidentialHistory'
import { 
  VerificationOfResidencyDetails,
  AccordionVerificationStatus,
  NoVerificationsPlaceholder,
} from 'shared/components/Verification'
import { VERIFICATION_ACCORDION_FOR_STATUS } from 'shared/components/Verification/utils'
import Tile from 'shared/components/Tile'
import { getFullnameData } from 'shared/utils/applicant'
import { VERIFICATION_TYPE } from 'shared/config/constants'
import { RentalCollections } from './components'
import { filterRentalCollections } from 'shared/utils/rentalCollections'

import {
  CreditScoreSummary,
} from 'shared/components/CreditAndBackgroundCheck/interfaces'
import { AddressPartial, DealPartial } from 'shared/components/CreditAndBackgroundCheck/types'
import { CollectionAccount } from 'shared/interfaces/creditAndBackgroundCheck'
import { COLORS } from 'shared/config/constants'


const TileCustom = styled(Tile)`
  .tile-header {
    align-items: center;
  }
  .tile-header-title-container {
    width: 100%;
    > div,
    h2 > div {
      width: 100%;
      align-items: center;
    }
    div {
    & > div {
        font-size: 15px;
        font-weight: 400;
      }
    }
    .main-header-left {
      &.vor {
        width: 100%;
        > div {
          width: 70%;
        }
      }
      .bold {
        font-size: 17px;
        font-weight: 500;
      }
    }
  }
  &.verified {
    background-color: ${COLORS.lightGreen};
  }
  &.unverified {
    background-color: ${COLORS.well.backgroundLightError};
  }
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`

const defaultCreditScoreSummary: Partial<{
  firstName: string
  middleName: string
  lastName: string
  addresses: AddressPartial[]
}> = {
  firstName: null,
  middleName: null,
  lastName: null,
  addresses: [],
}

/**
 * Background credit check data structure containing collections.
 * Subset of the full background check data needed for rental history display.
 */
interface BackgroundCreditCheckData {
  creditScoreSummary?: {
    collections?: CollectionAccount[]
  }
  isComplete?: boolean
}

/**
 * Verification data structure for residency verifications.
 * Supports multiple verification types with flexible status tracking.
 */
interface Verification {
  verificationType: string
  status: string
  verificationDate: string
  details?: any
  attributes?: {
    status?: string
  }
}

type RentalHistoryProps = {
  creditScoreSummary?: CreditScoreSummary | null
  evictions?: EvictionsType | null
  deal?: DealPartial | null
  application?: Application | null
  backgroundCreditCheck?: BackgroundCreditCheckData | null
  verifications?: Verification[]
  onRequestSurveyClick?: (id: number) => void
  isRequestingSurvey?: boolean
}

const N_A = 'N/A'

const RentalHistory = ({
  creditScoreSummary,
  evictions,
  deal = null,
  application = null,
  backgroundCreditCheck = null,
  verifications = [],
  onRequestSurveyClick = () => {},
  isRequestingSurvey = false
}: RentalHistoryProps) => {
  const { firstName, middleName, lastName } =
    creditScoreSummary ?? defaultCreditScoreSummary

  const applicantFullName =
    !!firstName || !!lastName
      ? [firstName, middleName, lastName].filter(Boolean).join(' ')
      : N_A

  const verificationOfResidency = verifications?.find(
    verification => verification?.verificationType === VERIFICATION_TYPE.VOR
  )

  // Get raw collections from credit report and filter to rental-only
  const rentalCollections = React.useMemo(() => {
    const rawCollections = backgroundCreditCheck?.creditScoreSummary?.collections
    if (!rawCollections) {
      return []
    }
    return filterRentalCollections(rawCollections)
  }, [backgroundCreditCheck?.creditScoreSummary?.collections])

  const renderVerification = (verification: Verification) => {
    return (
      <Tile.Inner>
        <VerificationOfResidencyDetails
          verification={verification as any}
          onRequestSurveyClick={onRequestSurveyClick}
          isRequestingSurvey={isRequestingSurvey}
        />
      </Tile.Inner>
    )
  }

  const dropHeader = (verification: Verification, verificationType: string) => {
    return (
      <HeaderContainer>
        <HeaderLeft className="main-header-left vor">
          <AccordionVerificationStatus
            type={verificationType as any}
            status={(verification.status || verification.attributes?.status) as any}
          />
        </HeaderLeft>
      </HeaderContainer>
    )
  }

  const verificationContent = ({ verification, verificationType }: { verification: Verification, verificationType: string }) => {
    return (
      <TileCustom
        className={
          VERIFICATION_ACCORDION_FOR_STATUS[
            verification.status || verification.attributes?.status
          ]
        }
        header={{
          title: dropHeader(verification, verificationType),
          collapsibleOnDesktop: true,
          collapsibleOnMobile: true,
        }}
      >
        <Tile key={0} noBorder noMarginBottom>
          {renderVerification(verification)}
        </Tile>
      </TileCustom>
    )
  }

  const getRentalCollectionBalance = (): string => {
    if (!rentalCollections?.length) {
      return '$0'
    }

    const openRecords = rentalCollections.filter(
      record => !record.dateClosed
    )

    const totalOpenBalance = openRecords.reduce(
      (sum, record) => sum + (record.currentBalance || 0),
      0
    )

    return printDollarsFromCents(totalOpenBalance)
  }

  const getRentalCollectionRecordsCount = (): number => {
    return rentalCollections?.length || 0
  }

  return (
    <>
      <ApplicantInfo
        creditScoreSummary={creditScoreSummary}
        deal={deal}
        applicantFullName={applicantFullName}
        application={application}
      />

      <Row gutter={14} type="flex">
        <Col xs={24} md={12} xl={12}>
          <Tile
            equalHeight
            header={{
              title: 'Eviction Related Proceedings',
            }}
          >
            <Tile.Inner>
              {isEvictionReportPending(evictions) ? (
                <div>Eviction records check is being processed</div>
              ) : !application?.isInNoEvictionState ? (
                <>
                  {evictions?.lastRecord && (
                    <>
                      <Tile.Label>Last Record</Tile.Label>
                      <strong>
                        State {evictions?.lastRecordState}
                      </strong>
                      <span>
                        {formatDate(evictions?.lastRecord?.date)}
                      </span>
                      <Tile.Label noMb>
                        {evictions?.lastRecord?.caseType}
                      </Tile.Label>
                    </>
                  )}
                  <Row>
                    <Col xs={8}>
                      <Tile.Label>States</Tile.Label>
                      <strong>
                        {evictions?.states}
                      </strong>
                    </Col>
                  </Row>
                </>
              ) : (
                <Tile.Label>
                  Eviction related proceedings are not considered in this state
                  or this county
                </Tile.Label>
              )}
            </Tile.Inner>
          </Tile>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Tile
            equalHeight
            header={{
              title: 'Rental Collections',
            }}
          >
            <Tile.Inner>
              <Row>
                <Col xs={12}>
                  <Tile.Label>Rental Collection Balance</Tile.Label>
                  <strong>
                    {getRentalCollectionBalance()}
                  </strong>
                </Col>
                <Col xs={12}>
                  <Tile.Label>Rental Collection Records</Tile.Label>
                  <strong>
                    {getRentalCollectionRecordsCount()}
                  </strong>
                </Col>
              </Row>
            </Tile.Inner>
          </Tile>
        </Col>
      </Row>

      <Evictions
        isEvictions={!application?.isInNoEvictionState}
        evictions={evictions}
        application={application}
      />

      <RentalCollections
        collections={rentalCollections}
      />

      <ResidentialHistory
        residentialHistory={application?.residentialHistories}
        residentialNonMatchingCriteriaNotes={application?.residentialNonMatchingCriteriaNotes}
        residentialDurationValid={application?.residentialDurationValid}
      />

      <Tile
        header={{
          title: 'Residency Verifications',
        }}
      >
        {(!verifications || !verificationOfResidency) ? (
          <Tile.Inner>
            <NoVerificationsPlaceholder message="No Residency Verifications Available" />
          </Tile.Inner>
        ) : (
          verificationContent({
            verification: verificationOfResidency,
            verificationType: VERIFICATION_TYPE.VOR,
          })
        )}
      </Tile>
    </>
  )
}

export default RentalHistory
